/**
 * SIL-2000 pure helpers for the pairing approval test selection: validating what the API sent, grouping devices by the profile
 * they are held to, and turning the technician's unchecked boxes into the approve request. No React here, so it is unit
 * testable; the server (the approve DEFINER function) is the only authority on what may be removed.
 */
import {
  benchPairingTestOptionsSchema,
  type BenchPairingTestDevice,
  type BenchPairingTestOptions,
  type BenchPairingTestStage,
  type BenchStageDeselection,
} from '@siliconx/shared';

/** The checkbox state is keyed by device index (unique per discovery) and stage index. */
export function removedKey(deviceIndex: number, stageIndex: number): string {
  return `${deviceIndex}:${stageIndex}`;
}

/**
 * The testOptions of a pairing lookup, or null when there is nothing to show: absent (flag off, attach pairing, discovery that
 * could not be read, empty catalog), malformed (a newer or older API: the page then approves with every test) or no devices.
 */
export function parseTestOptions(raw: unknown): BenchPairingTestOptions | null {
  if (raw === undefined || raw === null) return null;
  const parsed = benchPairingTestOptionsSchema.safeParse(raw);
  if (!parsed.success || parsed.data.devices.length === 0) return null;
  return parsed.data;
}

export interface ProfileGroup {
  key: string;
  profile: { id: string; version: number };
  devices: BenchPairingTestDevice[];
  /** Every device of a group is held to the same profile version, so the stage list is the group's. */
  stages: BenchPairingTestStage[];
}

export interface GroupedDevices {
  groups: ProfileGroup[];
  /** Devices with no matching profile (nvme / gpu): no tests run for them and they cannot grade like new. */
  unprofiled: BenchPairingTestDevice[];
}

export function groupDevices(options: BenchPairingTestOptions): GroupedDevices {
  const byKey = new Map<string, ProfileGroup>();
  const unprofiled: BenchPairingTestDevice[] = [];
  for (const d of options.devices) {
    if (!d.profile) {
      unprofiled.push(d);
      continue;
    }
    const key = `${d.profile.id}@${d.profile.version}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.devices.push(d);
      // The group's stage list is the first device's (same profile and version means the same stages), except that a device the
      // server locked for a shared deviceKey must not stand for the group: its optional stages read as not selectable.
      if (isSharedKeyLocked(existing.devices[0]!) && !isSharedKeyLocked(d)) existing.stages = d.stages;
    } else byKey.set(key, { key, profile: d.profile, devices: [d], stages: d.stages });
  }
  return { groups: [...byKey.values()], unprofiled };
}

/**
 * Whether the server locked this device because its real deviceKey is shared with another discovered device (the opaque
 * `device:<index>` keys cannot tell the two apart, so the server marks every optional stage `selectable: false` with the
 * `shared_device_key` reason and refuses a deselection on it). The UI does not detect sharing itself: it only reads this.
 */
export function isSharedKeyLocked(d: BenchPairingTestDevice): boolean {
  return d.stages.some((s) => !s.selectable && s.notSelectableReason === 'shared_device_key');
}

/** Whether the caller can remove at least one test from this device. */
export function deviceHasSelectableStage(d: BenchPairingTestDevice): boolean {
  return d.profile !== null && d.stages.some((s) => s.selectable);
}

export function anySelectableStage(options: BenchPairingTestOptions): boolean {
  return options.devices.some(deviceHasSelectableStage);
}

/**
 * The approve request body for the unchecked boxes. The deviceKey is the opaque `device:<index>` the server sent (every caller
 * gets it; the API maps it back). Empty for a caller who cannot deselect (a member must send nothing) and for any stage the
 * server marked not selectable (required, wipe, not observed, shared deviceKey), however the state got there.
 */
export function buildDeselections(options: BenchPairingTestOptions, removed: ReadonlySet<string>): BenchStageDeselection[] {
  if (!options.canDeselect) return [];
  const out: BenchStageDeselection[] = [];
  for (const d of options.devices) {
    if (!d.profile) continue;
    for (const s of d.stages) {
      if (!s.selectable || !removed.has(removedKey(d.deviceIndex, s.stageIndex))) continue;
      out.push({ deviceKey: d.deviceKey, profileId: d.profile.id, version: d.profile.version, stageIndex: s.stageIndex });
    }
  }
  return out;
}

/** "N optional tests removed on K devices" counts. */
export function summarizeDeselections(list: readonly BenchStageDeselection[]): { tests: number; devices: number } {
  return { tests: list.length, devices: new Set(list.map((d) => d.deviceKey)).size };
}

/** A device as the technician recognises it on the bench: kind, model and the masked serial the API sent. */
export function deviceLabel(d: BenchPairingTestDevice): string {
  const parts = [d.kind, d.model, d.serial].filter((p): p is string => typeof p === 'string' && p !== '');
  return parts.length > 0 ? parts.join(' ') : `device ${d.deviceIndex}`;
}

/** `deviceLabel` plus the discovery index, so two drives of one model with the same masked serial suffix stay distinguishable. */
export function deviceLabelWithIndex(d: BenchPairingTestDevice): string {
  return `${deviceLabel(d)} (#${d.deviceIndex})`;
}
